'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherKey } from '@subframe/core';
import { formatDate } from '@/shared/formatters';
import { Badge, Button, EmptyState } from '@/shared/components/ui';
import type { ChaveDoCliente, EstadoDaChave } from '../types/chavesDeApiTypes';
import { fimDaSobrevida, podeRevogar, podeRotacionar, rotuloDaChave } from '../utils/regrasDaTela';
const ROTULO_DO_ESTADO: Record<EstadoDaChave, string> = {
  VIGENTE: 'Vigente',
  EM_SOBREVIDA: 'Em sobrevida',
  REVOGADA: 'Revogada',
  EXPIRADA: 'Expirada'
};
const VARIANTE_DO_ESTADO: Record<EstadoDaChave, 'success' | 'warning' | 'neutral'> = {
  VIGENTE: 'success',
  EM_SOBREVIDA: 'warning',
  REVOGADA: 'neutral',
  EXPIRADA: 'neutral'
};
interface ListaDeChavesProps {
  chaves: readonly ChaveDoCliente[];
  /** Enquanto uma ação roda, os botões das linhas ficam desabilitados para não empilhar pedidos. */
  acoesDesabilitadas: boolean;
  onRotacionar: (chave: ChaveDoCliente) => void;
  onRevogar: (chave: ChaveDoCliente) => void;
}
export function ListaDeChaves(t0) {
  const $ = _c(17);
  const {
    chaves,
    acoesDesabilitadas,
    onRotacionar,
    onRevogar
  } = t0;
  if (chaves.length === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <EmptyState icon={<FeatherKey />} title="Nenhuma chave criada" description={"Crie uma chave para o sistema do escrit\xF3rio acessar a API da Arbitralis."} />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <th className="px-4 py-3 font-semibold">Chave</th>;
    t2 = <th className="px-4 py-3 font-semibold">Situação</th>;
    t3 = <th className="px-4 py-3 font-semibold">Criada em</th>;
    t4 = <th className="px-4 py-3 font-semibold">Criada por</th>;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <thead className="text-subtext-color bg-neutral-50 text-xs"><tr>{t1}{t2}{t3}{t4}<th className="px-4 py-3 font-semibold"><span className="sr-only">Ações</span></th></tr></thead>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== acoesDesabilitadas || $[7] !== chaves || $[8] !== onRevogar || $[9] !== onRotacionar) {
    let t7;
    if ($[11] !== acoesDesabilitadas || $[12] !== onRevogar || $[13] !== onRotacionar) {
      t7 = chave => <LinhaDaChave key={chave.id} chave={chave} acoesDesabilitadas={acoesDesabilitadas} onRotacionar={onRotacionar} onRevogar={onRevogar} />;
      $[11] = acoesDesabilitadas;
      $[12] = onRevogar;
      $[13] = onRotacionar;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    t6 = chaves.map(t7);
    $[6] = acoesDesabilitadas;
    $[7] = chaves;
    $[8] = onRevogar;
    $[9] = onRotacionar;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <div className="w-full overflow-x-auto rounded-md border border-solid border-neutral-200"><table className="w-full text-left text-sm">{t5}<tbody>{t6}</tbody></table></div>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
interface LinhaDaChaveProps extends Omit<ListaDeChavesProps, 'chaves'> {
  chave: ChaveDoCliente;
}
function LinhaDaChave(t0) {
  const $ = _c(48);
  const {
    chave,
    acoesDesabilitadas,
    onRotacionar,
    onRevogar
  } = t0;
  let t1;
  if ($[0] !== chave) {
    t1 = fimDaSobrevida(chave);
    $[0] = chave;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const expiraEm = t1;
  let t2;
  if ($[2] !== chave) {
    t2 = rotuloDaChave(chave);
    $[2] = chave;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const rotulo = t2;
  let t3;
  if ($[4] !== rotulo) {
    t3 = <span className="text-default-font font-medium">{rotulo}</span>;
    $[4] = rotulo;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== chave.keyPrefix || $[7] !== rotulo) {
    t4 = rotulo !== chave.keyPrefix && <span className="text-subtext-color font-mono text-xs">{chave.keyPrefix}</span>;
    $[6] = chave.keyPrefix;
    $[7] = rotulo;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <td className="px-4 py-3"><div className="flex flex-col gap-1">{t3}{t4}</div></td>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = VARIANTE_DO_ESTADO[chave.estado];
  const t7 = ROTULO_DO_ESTADO[chave.estado];
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <Badge variant={t6}>{t7}</Badge>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== expiraEm) {
    t9 = expiraEm && <span className="text-subtext-color text-xs">expira em {expiraEm}</span>;
    $[15] = expiraEm;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <td className="px-4 py-3"><div className="flex flex-col items-start gap-1">{t8}{t9}</div></td>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== chave.criadaEm) {
    t11 = formatDate(chave.criadaEm);
    $[20] = chave.criadaEm;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = <td className="text-default-font px-4 py-3">{t11}</td>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== chave.criadaPor.nome) {
    t13 = <span className="text-default-font">{chave.criadaPor.nome}</span>;
    $[24] = chave.criadaPor.nome;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== chave.criadorSemAcesso) {
    t14 = chave.criadorSemAcesso && <Badge variant="neutral">Sem acesso ao escritório</Badge>;
    $[26] = chave.criadorSemAcesso;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t13 || $[29] !== t14) {
    t15 = <td className="px-4 py-3"><div className="flex flex-col items-start gap-1">{t13}{t14}</div></td>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== acoesDesabilitadas || $[32] !== chave || $[33] !== onRotacionar) {
    t16 = podeRotacionar(chave) && <Button variant="neutral-secondary" size="small" disabled={acoesDesabilitadas} onClick={() => onRotacionar(chave)}>Rotacionar</Button>;
    $[31] = acoesDesabilitadas;
    $[32] = chave;
    $[33] = onRotacionar;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== acoesDesabilitadas || $[36] !== chave || $[37] !== onRevogar) {
    t17 = podeRevogar(chave) && <Button variant="destructive-secondary" size="small" disabled={acoesDesabilitadas} onClick={() => onRevogar(chave)}>Revogar agora</Button>;
    $[35] = acoesDesabilitadas;
    $[36] = chave;
    $[37] = onRevogar;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t16 || $[40] !== t17) {
    t18 = <td className="px-4 py-3"><div className="flex items-center justify-end gap-2">{t16}{t17}</div></td>;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t10 || $[43] !== t12 || $[44] !== t15 || $[45] !== t18 || $[46] !== t5) {
    t19 = <tr className="border-t border-solid border-neutral-200 align-top" data-testid="linha-da-chave">{t5}{t10}{t12}{t15}{t18}</tr>;
    $[42] = t10;
    $[43] = t12;
    $[44] = t15;
    $[45] = t18;
    $[46] = t5;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  return t19;
}